import { IdentityPicker } from '@/features/OrgMembers/components/IdentityPicker'
import type { MemberRowPatch } from '@/features/OrgMembers/hooks/useMemberRows'
import { blockedRowMessageKey } from '@/features/OrgMembers/lib/memberOutcomes'
import {
  type MemberRow,
  isRowBlocked,
} from '@/features/OrgMembers/lib/memberRows'
import type { OrganizationMemberRole } from '@/generated/http-clients/v3ApiSchemas'
import {
  Box,
  Button,
  Field,
  HStack,
  Icon,
  IconButton,
  Input,
  NativeSelect,
  Stack,
  Text,
} from '@chakra-ui/react'
import type { TFunction } from 'i18next'
import { Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'

const KEY_PREFIX = 'orgDetails.members.addMembers'

const ROLES: OrganizationMemberRole[] = ['viewer', 'admin']

type AddMemberRowProps = Readonly<{
  row: MemberRow
  disabled: boolean
  onChange: (key: string, patch: MemberRowPatch) => void
  onRemove: (key: string) => void
  onRetry: (key: string) => void
}>

function statusText(row: MemberRow, t: TFunction): string {
  switch (row.status) {
    case 'pending':
      return String(t(`${KEY_PREFIX}.row.pending`))
    case 'new':
      return String(t(`${KEY_PREFIX}.row.new`))
    case 'existing':
      return String(
        t(`${KEY_PREFIX}.row.existing`, {
          connection: row.identity.connection ?? row.identity.type,
          verification: t(
            row.identity.emailVerified
              ? `${KEY_PREFIX}.row.verified`
              : `${KEY_PREFIX}.row.unverified`,
          ),
        }),
      )
    case 'multiple':
      return String(t(`${KEY_PREFIX}.row.multiple`))
    default: {
      const message = blockedRowMessageKey(row)
      return message ? String(t(message.key, message.values ?? {})) : ''
    }
  }
}

export function AddMemberRow({
  row,
  disabled,
  onChange,
  onRemove,
  onRetry,
}: AddMemberRowProps) {
  const { t } = useTranslation('features', { useSuspense: false })
  const blocked = isRowBlocked(row)

  return (
    <Box borderWidth="1px" borderRadius="md" p={3}>
      <Stack gap={3}>
        <HStack justifyContent="space-between" alignItems="flex-start" gap={3}>
          <Stack gap={1} minW={0} flex={1}>
            <Text
              fontWeight="medium"
              textOverflow="ellipsis"
              overflow="hidden"
              whiteSpace="nowrap"
            >
              {row.email}
            </Text>
            <Text
              fontSize="sm"
              color={blocked ? 'fg.error' : 'text.text-subtle'}
            >
              {statusText(row, t)}
            </Text>
            {blocked && (
              <Text fontSize="sm" color="fg.error">
                {t(`${KEY_PREFIX}.row.notSent`)}
              </Text>
            )}
          </Stack>
          <HStack gap={2}>
            {row.status === 'lookupFailed' && (
              <Button
                size="xs"
                variant="outline"
                disabled={disabled}
                onClick={() => onRetry(row.key)}
              >
                {t(`${KEY_PREFIX}.row.retry`)}
              </Button>
            )}
            <IconButton
              aria-label={String(
                t(`${KEY_PREFIX}.row.remove`, { email: row.email }),
              )}
              size="xs"
              variant="ghost"
              disabled={disabled}
              onClick={() => onRemove(row.key)}
            >
              <Icon as={Trash2} />
            </IconButton>
          </HStack>
        </HStack>

        {!blocked && (
          <Field.Root disabled={disabled}>
            <Field.Label>{t(`${KEY_PREFIX}.row.role`)}</Field.Label>
            <NativeSelect.Root size="sm" disabled={disabled}>
              <NativeSelect.Field
                value={row.role}
                onChange={(event) =>
                  onChange(row.key, {
                    role: event.target.value as OrganizationMemberRole,
                  })
                }
              >
                {ROLES.map((role) => (
                  <option key={role} value={role}>
                    {String(t(`${KEY_PREFIX}.row.roles.${role}`))}
                  </option>
                ))}
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>
          </Field.Root>
        )}

        {row.status === 'new' && (
          <HStack gap={3} alignItems="flex-start">
            <Field.Root disabled={disabled}>
              <Field.Label>{t(`${KEY_PREFIX}.row.firstName`)}</Field.Label>
              <Input
                size="sm"
                value={row.firstName}
                disabled={disabled}
                onChange={(event) =>
                  onChange(row.key, { firstName: event.target.value })
                }
              />
            </Field.Root>
            <Field.Root disabled={disabled}>
              <Field.Label>{t(`${KEY_PREFIX}.row.lastName`)}</Field.Label>
              <Input
                size="sm"
                value={row.lastName}
                disabled={disabled}
                onChange={(event) =>
                  onChange(row.key, { lastName: event.target.value })
                }
              />
            </Field.Root>
          </HStack>
        )}

        {row.status === 'multiple' && (
          <IdentityPicker
            identities={row.identities}
            selectedUserId={row.selectedUserId}
            memberUserIds={row.memberUserIds}
            disabled={disabled}
            onSelect={(userId) => onChange(row.key, { selectedUserId: userId })}
          />
        )}
      </Stack>
    </Box>
  )
}
